import { action, makeObservable, observable } from "mobx";
import HrmsAPI from "../api/HrmsAPI";
import {
  EmployeeCountsResult,
  HrmsEmployee,
  HrmsEmployeeListParams,
  HrmsSyncEventRow,
  HrmsSyncEventsListParams,
} from "../models/Employees/HrmsEmployee";

export class HrmsStore {
  hrmsAPI: HrmsAPI;
  employees: HrmsEmployee[];
  limit: number;
  total_pages: number;
  total_count: number;
  counts: EmployeeCountsResult;
  isLoadingList: boolean;
  isLoadingCounts: boolean;
  syncEvents: HrmsSyncEventRow[];
  syncEventsPage: number;
  syncEventsLimit: number;
  syncEventsTotalCount: number;
  isLoadingSyncEvents: boolean;
  syncEventsErrorMessage: string | null;
  errorMessage: string | null;

  constructor() {
    this.hrmsAPI = new HrmsAPI();
    this.employees = [];
    this.limit = 30;
    this.total_pages = 0;
    this.total_count = 0;
    this.counts = {
      active: "0",
      inactive: "0",
      new_employees: "0",
      total: 0,
    };
    this.isLoadingList = false;
    this.isLoadingCounts = false;
    this.syncEvents = [];
    this.syncEventsPage = 1;
    this.syncEventsLimit = 20;
    this.syncEventsTotalCount = 0;
    this.isLoadingSyncEvents = false;
    this.syncEventsErrorMessage = null;
    this.errorMessage = null;

    makeObservable(this, {
      employees: observable,
      limit: observable,
      total_pages: observable,
      total_count: observable,
      counts: observable,
      isLoadingList: observable,
      isLoadingCounts: observable,
      syncEvents: observable,
      syncEventsPage: observable,
      syncEventsLimit: observable,
      syncEventsTotalCount: observable,
      isLoadingSyncEvents: observable,
      syncEventsErrorMessage: observable,
      errorMessage: observable,
      getEmployees: action,
      getEmployeeCounts: action,
      getSyncEvents: action,
      setSyncEventsPage: action,
      setSyncEventsLimit: action,
      reset: action,
    });
  }

  reset = () => {
    this.employees = [];
    this.limit = 30;
    this.total_pages = 0;
    this.total_count = 0;
    this.counts = {
      active: "0",
      inactive: "0",
      new_employees: "0",
      total: 0,
    };
    this.isLoadingList = false;
    this.isLoadingCounts = false;
    this.syncEvents = [];
    this.syncEventsPage = 1;
    this.syncEventsLimit = 20;
    this.syncEventsTotalCount = 0;
    this.isLoadingSyncEvents = false;
    this.syncEventsErrorMessage = null;
    this.errorMessage = null;
  };

  getEmployees = async (params: HrmsEmployeeListParams) => {
    this.isLoadingList = true;
    this.errorMessage = null;
    try {
      const response = await this.hrmsAPI.getEmployees(params);
      this.employees = response.result?.list || [];
      this.limit = response.result?.limit || params.limit;
      this.total_count = response.result?.total_count || 0;
      this.total_pages = response.result?.total_pages || 0;
      return response;
    } catch (error: any) {
      this.errorMessage =
        error?.response?.data?.message || "Failed to fetch employees list.";
      throw error;
    } finally {
      this.isLoadingList = false;
    }
  };

  getEmployeeCounts = async () => {
    this.isLoadingCounts = true;
    try {
      const response = await this.hrmsAPI.getEmployeeCounts();
      if (response?.result) this.counts = response.result;
      return response;
    } catch (error: any) {
      throw error;
    } finally {
      this.isLoadingCounts = false;
    }
  };

  setSyncEventsPage = (page: number) => {
    this.syncEventsPage = page;
  };

  setSyncEventsLimit = (limit: number) => {
    this.syncEventsLimit = limit;
    this.syncEventsPage = 1;
  };

  getSyncEvents = async (params: HrmsSyncEventsListParams) => {
    this.isLoadingSyncEvents = true;
    this.syncEventsErrorMessage = null;
    try {
      const response = await this.hrmsAPI.getSyncEvents(params);
      this.syncEvents = response?.result?.rows || [];
      this.syncEventsPage = response?.result?.pagination?.page || params.page;
      this.syncEventsLimit = response?.result?.pagination?.limit || params.limit;
      this.syncEventsTotalCount = response?.result?.pagination?.total || 0;
      return response;
    } catch (error: any) {
      this.syncEventsErrorMessage =
        error?.response?.data?.message || "Failed to fetch GetKnit sync events.";
      throw error;
    } finally {
      this.isLoadingSyncEvents = false;
    }
  };
}
